import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import type { IDynamicElement } from '../_shared/models/dynamic/dynamic-element.model';
import type { IFeatureList, IFeatureListItem } from '../_shared/models/feature-list.model';
import { withConfigFallback } from '../_shared/utils/with-config-fallback';
import { ContainerComponent } from '../container/container.component';
import { HeadingComponent } from '../heading/heading.component';
import { IconComponent } from '../icon/icon.component';
import { isIconName } from '../icon/icon-names';
import { TextHtmlComponent } from '../text-html/text-html.component';

@Component({
    selector: 'sirius-feature-list',
    templateUrl: './feature-list.component.html',
    styleUrls: ['./feature-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ContainerComponent, HeadingComponent, IconComponent, TextHtmlComponent],
    host: {
        'class': 'sir-feature-list sir-text-style-paragraph-m-default',
        '[class.has-icons]': 'hasIcons()',
    },
})
export class FeatureListComponent implements IDynamicElement<IFeatureList> {
    readonly config = input<IFeatureList | undefined>(undefined);

    readonly title = input('');
    readonly textHtml = input('');
    readonly items = input<IFeatureListItem[]>([]);

    protected readonly resolvedTitle = withConfigFallback<IFeatureList, 'title', string>(
        this.config,
        'title',
        this.title
    );
    protected readonly resolvedTextHtml = withConfigFallback<IFeatureList, 'textHtml', string>(
        this.config,
        'textHtml',
        this.textHtml
    );
    private readonly resolvedItems = withConfigFallback<IFeatureList, 'items', IFeatureListItem[]>(
        this.config,
        'items',
        this.items
    );

    // Icon names come from content, and IconService throws on unknown ones.
    protected readonly rows = computed(() =>
        this.resolvedItems().map((item) => ({ ...item, iconName: isIconName(item.icon) ? item.icon : undefined }))
    );

    protected readonly hasIcons = computed(() => this.rows().some((row) => row.iconName));
}
